import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
const ps = new WeakMap();

const TRAILING_QUESTION_AMPERSAND_REGEX = /[?&]$/;

/**
 * Transport
 * An API that provides client functionality for transferring data between a client and a server.
 * @author Sergey Arakelov <sergey.arakelov@bankofamerica.com>
 */
export default class Transport {
  /**
   * Constructor
   * @param {object} option
   * @param {method} options.beforeSend
   * @param {boolean} options.cache
   * @param {boolean} options.crossDomain
   * @param {string|boolean} options.contentType
   * @param {object} options.data
   * @param {string} options.dataType
   * @param {object} options.headers
   * @param {method} options.method
   * @param {method} options.error
   * @param {method} options.success
   * @param {method} options.onTimeout
   * @param {number} options.timeout
   * @param {string} options.url
   * @param {object} options.xhrFields
   */
  constructor(opts, globalConfig) {
    const defaults = {
      cache: true,
      data: null,
      headers: {}, // eslint-disable-line no-unused-vars
      method: 'GET',
      timeout: 0,
      xhrFields: {
        widthCredentials: false,
      },
    };

    const options = { ...defaults, ...opts };
    const { beforeSend, cache, complete, data, error, method, success, timeout, xhrFields } = options; // eslint-disable-line max-len

    // defaulting contentType for non GET requests
    let { crossDomain, contentType } = options;
    if (typeof contentType === 'undefined' && ['GET', 'HEAD'].indexOf(method) === -1) contentType = 'application/x-www-form-urlencoded; charset=UTF-8';

    const {
      globalError,
      globalComplete,
      globalSuccess,
    } = globalConfig;

    // create a new xhr instance
    const xhr = new XMLHttpRequest();

    // capturing the native xhr.getResponseHeader
    const nativeGetResponseHeader = xhr.getResponseHeader.bind(xhr);
    const nativeSetRequestHeader = xhr.setRequestHeader.bind(xhr);
    const nativeXHRAbort = xhr.abort.bind(xhr);

    xhr.abort = () => { this.options.shouldAbort = true; };

    // setting private data to a WeakMap
    ps.set(this, {
      xhr,
      status,
      ...options,
      globalConfig,
      nativeGetResponseHeader,
      nativeSetRequestHeader,
      nativeXHRAbort,
      completed: false,
      called: {},
    });

    const urlObj = Transport.getUrlInfo(this.options.url);
    const reqData = { ...this.options.data };

    // auto determine cross domain when its undefined
    if (typeof crossDomain === 'undefined') crossDomain = window.location.origin !== urlObj.origin;

    // applying the args
    let urlRequestString = `${this.options.url}${urlObj.search ? '&' : '?'}`;

    // when contentType is application/data and data is a string, just send it as is
    let payload = contentType && contentType.indexOf('application/json') !== -1 && typeof data === 'string' ? data : this.buildUrlQueryString(data);
    payload = payload === '' ? null : payload;

    // apply caching
    if (['GET', 'HEAD'].indexOf(method) > -1 && (cache === false || this.dataType === 'script')) {
      reqData._ = (new Date()).getTime();
    }

    // attach urlQueryString to urlRequestString for GET requests
    if (['GET', 'HEAD'].indexOf(method) > -1 && urlRequestString.length) {
      urlRequestString = `${urlRequestString}${urlRequestString.match(/\?$/) || urlObj.search ? '' : '?'}${this.buildUrlQueryString(reqData)}`;
    }

    // attach timestamp to query to avoid caching
    urlRequestString = urlRequestString
    .replace(TRAILING_QUESTION_AMPERSAND_REGEX, '')
    .replace(TRAILING_QUESTION_AMPERSAND_REGEX, '');

    // faking xhr.setRequestHeader to allow headers to be set in beforeSend
    this.xhr.setRequestHeader = this.setRequestHeader.bind(this);

    // handle beforeSend
    if (beforeSend && typeof beforeSend === 'function') {
      const beforeSendResult = beforeSend.call(this.options, this.xhr, this.options);
      if (beforeSendResult === false) return;

      this.xhr.abort = nativeXHRAbort;

      ps.set(this, {
        ...options,
        ...ps.get(this),
        ...this.options,
      });

      if (this.options.url !== options.url) urlRequestString = this.options.url;
    }

    const reqHeaders = { ...this.options.headers };

    // X-Requested-With
    if (!crossDomain && !reqHeaders['X-Requested-With']) {
      reqHeaders['X-Requested-With'] = 'XMLHttpRequest';
    }

    // set request the content type
    // ignore if headers['Content-Type'] already exists
    if (!reqHeaders['Content-Type'] && contentType && contentType !== false) {
      reqHeaders['Content-Type'] = contentType;
    }

    // open the request
    this.xhr.open(method, urlRequestString, true);

    // Set headers
    for (const i in reqHeaders) { // eslint-disable-line
      nativeSetRequestHeader(i, reqHeaders[i]);
    }

    // adding some protection
    this.xhr.withCredentials = xhrFields.withCredentials;

    // faking xhr.getResponseHeader to suppress the unsafe header warning
    this.xhr.getResponseHeader = this.getResponseHeader.bind(this);

    // handle xhr state change
    this.xhr.onreadystatechange = () => {
      // Handling success
      if (
        (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 200 && xhr.status < 300)
        || xhr.status === 304
      ) {
        this.onSuccess(success);

        if (globalSuccess && globalSuccess.doc && globalSuccess.cb) {
          this.onGlobalSuccess(globalSuccess.doc, globalSuccess.cb);
        }

        ps.get(this).completed = true;
      }

      // Handling errors
      if (
        (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 0)
        || (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED && xhr.status >= 400)
      ) {
        // call onError when it hasnt been aborted
        if (!this.options.called.error) this.onError(error);

        if (!this.options.called.globalError && globalError && globalError.doc && globalError.cb) {
          this.onGlobalError(
            globalError.doc,
            globalError.cb,
          );
        }
      }

      // Handling complete
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (!this.options.called.complete) this.onComplete(complete);

        if (
          !this.options.called.globalComplete
          && globalComplete
          && globalComplete.doc
          && globalComplete.cb
        ) {
          this.onGlobalComplete(globalComplete.doc, globalComplete.cb);
        }
      }
    };

    this.xhr.ontimeout = () => {
      this.onError(error, 'timeout');

      if (globalError && globalError.doc && globalError.cb) {
        this.onGlobalError(globalError.doc, globalError.cb, 'timeout');
      }
    };

    // set timeout
    this.xhr.timeout = timeout;

    // sending the request
    if (!this.options.shouldAbort) this.xhr.send(payload);
  }

  /**
   * fakeAbort
   * The purpose of this is to allow user to abort in the beforeSend
   */
  fakeAbort() {
    this.options.shouldAbort = true;
  }

  /**
   * Abort the request
   */
  abort() {
    this.options.called.error = true;
    this.options.called.complete = true;

    this.xhr.abort();
    this.onError(this.options.error, 'abort');
    this.onComplete(this.options.complete);

    if (
      !this.options.called.globalComplete
      && this.options.globalConfig.globalComplete
      && this.options.globalConfig.globalComplete.doc
      && this.options.globalConfig.globalComplete.cb
    ) {
      this.onGlobalComplete(
        this.options.globalConfig.globalComplete.doc,
        this.options.globalConfig.globalComplete.cb,
      );
    }
  }

  /**
   * buildUrlQueryString
   * @param {object} data
   * @param {string} prefix
   */
  buildUrlQueryString(obj, prefix) {
    const str = [];

    for (const p in obj) { // eslint-disable-line no-restricted-syntax
      if (Object.prototype.hasOwnProperty.call(obj, p)) {
        const k = prefix ? `${prefix}[${p}]` : p;
        const v = obj[p];
        const qStr = (v !== null && typeof v === 'object')
        ? this.buildUrlQueryString(v, k)
        : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;

        str.push(qStr);
      }
    }

    return str.join('&');
  }

  /**
   * Get absolute url
   * @param {string} url URL string
   */
  static getUrlInfo(url) { // eslint-disable-line class-methods-use-this
    let a;
    if (!a) a = document.createElement('a');
    a.href = url;
    return a;
  }

  /**
   * onGlobalSuccess
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalSuccessCB Callback method
   */
  onGlobalSuccess(doc, onGlobalSuccessCB) {
    if (doc === document && typeof onGlobalSuccessCB === 'function') {
      onGlobalSuccessCB.call(doc, {
        currentTarget: doc,
        target: doc,
      }, this.xhr, this.options, this.getXHRData());
    }
  }

  /**
   * onGlobalError
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalErrorCB Callback method
   */
  onGlobalError(doc, onGlobalErrorCB, status = 'error') {
    this.options.called.globalError = true;

    if (doc === document && typeof onGlobalErrorCB === 'function') {
      onGlobalErrorCB.call(doc, {
        currentTarget: doc,
        target: doc,
      }, this.xhr, this.options, status);
    }
  }

  /**
   * onGlobalComplete
   * Global error hook
   * @param {object} doc Window document
   * @param {method} onGlobalCompleteCB Callback method
   */
  onGlobalComplete(doc, onGlobalCompleteCB) {
    this.options.called.globalComplete = true;

    if (doc === document && typeof onGlobalCompleteCB === 'function') {
      onGlobalCompleteCB.call(doc, {
        currentTarget: doc,
        target: doc,
      }, this.xhr, this.options);
    }
  }

  /**
   * onError handler
   * @param {method} onErrorCB User defined onError callback
   */
  onError(onErrorCB, status = 'error') {
    this.options.called.error = true;

    if (typeof onErrorCB === 'function') {
      this.status = status;
      const statusText = this.xhr.statusText === '' ? status : this.xhr.statusText;
      onErrorCB(this.xhr, this.status, statusText);
    }

    return null;
  }

  /**
   * onComplete handler
   * Called after error or success callbacks
   * @param {method} onCompleteCB User defined onComplete callback
   */
  onComplete(onCompleteCB) {
    this.options.called.complete = true;

    if (onCompleteCB && typeof onCompleteCB === 'function') {
      onCompleteCB.call(this.options, this.xhr, this.status);
    }

    return null;
  }

  /**
   * onSuccess handler
   * @param {method} onSuccessCB User defined onSuccess callback
   */
  onSuccess(onSuccessCB) {
    this.options.called.success = true;

    const data = this.getXHRData();

    // call users onSuccessCB method
    if (onSuccessCB && typeof onSuccessCB === 'function') {
      this.status = 'success';
      return onSuccessCB.call(this.options, data, this.status, this.xhr);
    }

    return null;
  }

  /**
   * Get the conformed xhr data based on the dataType or content-type
   */
  getXHRData() {
    const contentTypes = {
      json: 'application/json',
      xml: 'application/xml',
      html: 'text/html',
      script: 'application/javascript',
    };

    if (!this.dataType) {
      for (const type in contentTypes) { // eslint-disable-line no-restricted-syntax
        if (this.xhr.getResponseHeader('Content-Type').indexOf(type) !== -1) this.dataType = type;
      }
    }

    if (this.xhr.responseText === '') return undefined;

    // handling the dataType
    switch (this.dataType) {
      case 'json':
        try {
          return JSON.parse(this.xhr.responseText);
        } catch(e) {
          return undefined;
        }
      case 'xml':
        return this.xhr.responseXML;
      case 'script':
      case 'html':
      default:
        return this.xhr.responseText;
    }
  }

  /**
   * Cross browser XML parsing
   * @param {string} xmlData XML Data
   */
  parseXML(xmlData) { // eslint-disable-line class-methods-use-this
    let xml;

    if (!xmlData || typeof xmlData !== 'string') return null;

    // Support: IE 9 - 11 only
    // IE throws on parseFromString with invalid input.
    try {
      xml = (new window.DOMParser()).parseFromString(xmlData, 'text/xml');
    } catch (e) { /* noop */ }

    if (!xml || xml.getElementsByTagName('parsererror').length) {
      throw new Error(`Invalid XML: ${xmlData}`);
    }

    return xml;
  }

  /**
   * setRequestHeader
   * Set a request header
   * @param {string} key Key Name
   * @param {string} val Value
   */
  setRequestHeader(key, val) {
    this.options.headers[key] = val;
  }

  /**
   * Content headers value lookup by key name
   * @param {string} key Key name
   */
  getResponseHeader(key) {
    const rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg;
    const responseHeaders = {};
    let match;

    while ((match = rheaders.exec(this.xhr.getAllResponseHeaders()))) { // eslint-disable-line no-cond-assign,max-len
      responseHeaders[match[1].toLowerCase()] = match[2];
    }

    match = responseHeaders[key.toLowerCase()];
    return match;
  }

  /**
   * Get the current options for the XHR request
   */
  get options() {
    return ps.get(this);
  }

  /**
   * Set the current status of the xhr request
   */
  set status(status) {
    ps.get(this).status = status;
  }

  /**
   * Get the current status of the xhr request
   */
  get status() {
    return ps.get(this).status;
  }

  /**
   * Set the dataType
   */
  set dataType(type) {
    this.options.dataType = type;
  }

  /**
   * Get the dataType
   */
  get dataType() {
    return this.options.dataType;
  }

  /**
   * Get the XHR object
   */
  get xhr() {
    return ps.get(this).xhr;
  }
}
